import type { FirebaseOptions } from 'firebase/app';
import { getCurrentRuntimeTarget, RuntimeTarget } from './runtime-target';
import { getBrand } from '../app/constants/brands';
import type { BrandConfig } from '../app/constants/brands';

const DEFAULT_FIREBASE_CONFIGS: Record<'development' | 'production', FirebaseOptions> = {
  development: {
    apiKey: 'AIzaSyCv-1C5beMfhgcw2EXObPnQCVNsVHYCNmI',
    authDomain: 'autopiloteern-dev.firebaseapp.com',
    projectId: 'autopiloteern-dev',
    storageBucket: 'autopiloteern-dev.firebasestorage.app',
    messagingSenderId: '189111663619',
    appId: '1:189111663619:web:d4970458b97c7378d1e236',
    measurementId: 'G-JSVLKRJ7TD',
  },
  production: {
    apiKey: 'AIzaSyDllY0X3nguGHLkxJhoKZYzKECfjjErwss',
    authDomain: 'autopiloteern-prod.firebaseapp.com',
    projectId: 'autopiloteern-prod',
    storageBucket: 'autopiloteern-prod.firebasestorage.app',
    messagingSenderId: '899729935933',
    appId: '1:899729935933:web:b0d0fe59320f2ee6d86454',
    measurementId: 'G-D175V6WKKY',
  },
};

const ENV_FIREBASE_KEYS = {
  apiKey: 'REACT_APP_FIREBASE_API_KEY',
  authDomain: 'REACT_APP_FIREBASE_AUTH_DOMAIN',
  projectId: 'REACT_APP_FIREBASE_PROJECT_ID',
  storageBucket: 'REACT_APP_FIREBASE_STORAGE_BUCKET',
  messagingSenderId: 'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
  appId: 'REACT_APP_FIREBASE_APP_ID',
  measurementId: 'REACT_APP_FIREBASE_MEASUREMENT_ID',
} as const;

function getEnvFirebaseConfig(required: boolean): FirebaseOptions | null {
  const config = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  };

  // NOTE: use the already-resolved `config` values (static access above) to detect
  // missing keys — do NOT use process.env[envKey] (dynamic) which DefinePlugin
  // cannot replace and always evaluates to undefined in the browser bundle.
  const missing = (
    ['apiKey', 'authDomain', 'projectId', 'storageBucket', 'messagingSenderId', 'appId'] as const
  ).filter(key => !config[key]);

  if (missing.length > 0) {
    if (!required) return null;
    throw new Error(`Missing Firebase config for staging: ${missing.join(', ')}`);
  }

  return config as FirebaseOptions;
}

type BrandFirebaseSource = Pick<BrandConfig, 'firebase'>;

/**
 * The Firebase web config for this page. A brand with its own project
 * (`brand.firebase`) uses it; every other brand uses the platform project,
 * the same rule the backend applies (D13). Staging is one env-configured
 * project for every brand host.
 */
export function getFirebaseConfig(
  target: RuntimeTarget = getCurrentRuntimeTarget(),
  brand: BrandFirebaseSource = getBrand(),
): FirebaseOptions {
  if (target === 'staging') {
    return getEnvFirebaseConfig(true)!;
  }

  return brand.firebase?.[target] ?? DEFAULT_FIREBASE_CONFIGS[target];
}

export function getFirebaseVapidKey(
  brand: BrandFirebaseSource = getBrand(),
  target: RuntimeTarget = getCurrentRuntimeTarget(),
): string | undefined {
  if (target !== 'staging' && brand.firebase?.[target] && brand.firebase.vapidKey) {
    return brand.firebase.vapidKey;
  }
  return process.env.REACT_APP_FIREBASE_VAPID_KEY;
}

/**
 * Service worker URL carrying this page's Firebase config and notification
 * icon. The worker cannot read the brand or the build target, and production
 * builds emit no config file for it, so before this it guessed by hostname
 * and gave every host but autopilotee.com the development project.
 */
export function firebaseMessagingServiceWorkerUrl(config: FirebaseOptions, icon: string): string {
  const params = new URLSearchParams({ config: JSON.stringify(config), icon });
  return `/firebase-messaging-sw.js?${params.toString()}`;
}
